import { gql, useMutation, useQuery } from "@apollo/client";
import { AdvancedImage, lazyload } from "@cloudinary/react";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { components } from "react-select";
import { cloudinary } from "src/services/cloudinary";
import { errorToast, successToast } from "src/utils/toast";
import { DEFAULT_CLOUDINARY_IMAGE } from "src/utils/variables";
import styled from "styled-components";
import { ModalContext } from "../../context";
import { theme } from "../../utils/theme";
import { AppText, FlexDiv } from "../UI";
import { PhoenixAppButton, PhoenixMultiSelect } from "../UI/Phoenix";
import { Modal } from "./Modal";

const FETCH_REPS = gql`
  query fetchReps {
    fetchOrganization {
      id
      Reps {
        id
        full_name
        profile_image
      }
    }
  }
`;

const FETCH_DISPOSITION_OPTIONS = gql`
  query getDispositionChangeOptions($lead_activity_id: String!) {
    getDispositionChangeOptions(lead_activity_id: $lead_activity_id) {
      disposition_id
      label
    }
  }
`;

const UPDATE_DISPOSITION_TYPE = gql`
  mutation changeDispositionOption($disposition_id: String!, $lead_activity_id: String!) {
    changeDispositionOption(disposition_id: $disposition_id, lead_activity_id: $lead_activity_id) {
      id
      published
      channel
      conference_id
      title
      type
    }
  }
`;

const CHANGE_DISPOSITION_REP = gql`
  mutation changeDispositionRep($new_rep_id: String!, $lead_activity_id: String!) {
    changeDispositionRep(new_rep_id: $new_rep_id, lead_activity_id: $lead_activity_id) {
      id
      published
      channel
      notes
      conference_id
      title
      type
    }
  }
`;

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <FlexDiv align="center" gap={8}>
        {!!props?.data?.value && (
          <AdvancedImageStyle cldImg={cloudinary.image(props?.data?.profile_image)} plugins={[lazyload()]} />
        )}
        {props?.data?.label}
      </FlexDiv>
    </components.Option>
  );
};

export const EditCallResultModal: React.FC = ({}) => {
  const { showEditCallResultModal, setShowEditCallResultModal, editCallResultModalData } = useContext(ModalContext);

  const [selectedRep, setSelectedRep] = useState<string | undefined>(editCallResultModalData?.rep_id);
  const [selectedDisposition, setSelectedDisposition] = useState<string | undefined>(
    editCallResultModalData?.related_disposition?.id,
  );

  const { data: dataOrg, loading: loadingOrg } = useQuery(FETCH_REPS, {
    fetchPolicy: "cache-and-network",
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });
  const { data: dataDispositionOptions, loading: loadingDispositionOptions } = useQuery(FETCH_DISPOSITION_OPTIONS, {
    fetchPolicy: "cache-and-network",
    variables: {
      lead_activity_id: editCallResultModalData?.lead_activity_id,
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const repOptions = useMemo(() => {
    const reps = dataOrg?.fetchOrganization?.Reps?.slice() || [];
    if (!reps.length)
      return [
        {
          value: undefined,
          label: "No Available Reps",
          disabled: true,
        },
      ];
    return reps
      ?.sort((a: any, b: any) => a?.full_name?.localeCompare(b?.full_name) ?? 0)
      ?.map((rep: any) => ({
        value: rep?.id,
        label: rep?.full_name ?? "N/A",
        profile_image: rep?.profile_image || DEFAULT_CLOUDINARY_IMAGE,
      }));
  }, [dataOrg, loadingOrg]);

  const dispositionOptions = useMemo(() => {
    const dispositions = dataDispositionOptions?.getDispositionChangeOptions?.slice() || [];
    return (
      dispositions
        ?.sort((a: any, b: any) => a?.label?.localeCompare(b?.label) ?? 0)
        ?.map((disposition: any) => ({
          value: disposition?.disposition_id,
          label: disposition?.label ?? "No Call Result",
        })) ?? []
    );
  }, [dataDispositionOptions]);

  const [changeType, { loading: loadingChangeType }] = useMutation(UPDATE_DISPOSITION_TYPE, {
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
    refetchQueries: ["fetchAggregatedLeadHistory"],
  });

  const [changeRep, { loading: loadingChangeRep }] = useMutation(CHANGE_DISPOSITION_REP, {
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
    refetchQueries: ["fetchAggregatedLeadHistory"],
  });

  const handleSubmit = useCallback(async () => {
    try {
      await changeRep({
        variables: {
          new_rep_id: selectedRep,
          lead_activity_id: editCallResultModalData.lead_activity_id,
        },
      });
      await changeType({
        variables: {
          disposition_id: selectedDisposition,
          lead_activity_id: editCallResultModalData.lead_activity_id,
        },
      });

      successToast("Call Result Updated!");
    } catch (error) {
      errorToast("Failed to update call result.");
    } finally {
      setShowEditCallResultModal(false);
    }
  }, [selectedRep, editCallResultModalData, selectedDisposition]);

  return (
    <Modal
      open={showEditCallResultModal}
      onClose={() => {
        setShowEditCallResultModal(false);
      }}
      popupAnimation
    >
      <Main>
        <ModalBody>
          <AppText fontSize={16} fontWeight={600}>
            Edit Call Result
          </AppText>

          <FlexDiv direction="column" gap={16}>
            <FlexDiv direction="column" gap={8}>
              <AppText fontSize={12} fontWeight={500} lineHeight={18}>
                Call Result
              </AppText>
              <PhoenixMultiSelect
                isMulti={false}
                name="call_result"
                options={dispositionOptions}
                placeholder="Select a Call Result"
                onChange={(e: { label: string; value: string }) => setSelectedDisposition(e?.value)}
                value={dispositionOptions?.find((option: any) => option.value === selectedDisposition) || undefined}
                isClearable={false}
                marginBottom={false}
                error={false}
                pointer
                width={396}
              />
            </FlexDiv>
            <FlexDiv direction="column" gap={8}>
              <AppText fontSize={12} fontWeight={500} lineHeight={18}>
                Rep
              </AppText>
              <PhoenixMultiSelect
                isMulti={false}
                name="rep"
                options={repOptions}
                placeholder="Select a Rep"
                onChange={(e: { label: string; value: string }) => setSelectedRep(e?.value)}
                value={repOptions?.find((option: any) => option.value === selectedRep) || undefined}
                isClearable={false}
                marginBottom={false}
                error={false}
                pointer
                width={396}
                isDisabled={loadingOrg}
                isOptionDisabled={(option) => option.disabled || loadingOrg}
                components={{ Option }}
              />
            </FlexDiv>
          </FlexDiv>
        </ModalBody>

        <ModalButtonContainer>
          <PhoenixAppButton
            buttonType="secondary"
            variant="brand-outline"
            uppercase
            buttonTextFontSize={10}
            style={{ letterSpacing: "1px" }}
            onClick={() => {
              setShowEditCallResultModal(false);
            }}
          >
            Cancel
          </PhoenixAppButton>
          <PhoenixAppButton
            buttonType="secondary"
            uppercase
            buttonTextFontSize={10}
            style={{ letterSpacing: "1px" }}
            onClick={handleSubmit}
            disabled={
              loadingDispositionOptions ||
              loadingOrg ||
              !selectedDisposition ||
              !selectedRep ||
              loadingChangeRep ||
              loadingChangeType
            }
          >
            Continue
          </PhoenixAppButton>
        </ModalButtonContainer>
      </Main>
    </Modal>
  );
};

const Main = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 476px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;

  width: 100%;
  padding: 40px 40px 32px 40px;

  text-align: center;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding: 16px 40px;

  border-top: 1px solid ${theme.NEUTRAL200};
`;

const AdvancedImageStyle = styled(AdvancedImage)`
  border-radius: 24px;
  width: 24px;
  height: 24px;
`;
